import { KeyboardEvent, useId, useRef, useState } from 'react'
import {
  activeVariableQuery,
  CanonicalVariableOption,
  filterCanonicalOptions,
  insertCanonicalToken,
} from 'src/services/whatsapp/bindings'
import { TemplateVariablePicker } from './template-variable-picker'

interface TemplateBodyInputProps {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  // Whether the org has a TRA/SIRE-reporting property, which unlocks the
  // check-in survey link option.
  hasReportingProperties?: boolean
  'data-testid': string
}

/**
 * Template body textarea that opens a canonical-variable picker while the admin
 * types `{{` — the authoring half of template-variable-picker. Shared by the
 * create and edit forms so the trigger/filter/insert behavior stays one
 * implementation. Selecting an option inserts its token; the variable's
 * source/field binding then follows from the token text itself, derived by
 * `useTemplateBindings` (see `canonicalBindingFor`) — this component only ever
 * edits body text.
 */
export const TemplateBodyInput = ({
  value,
  onChange,
  onBlur,
  placeholder,
  hasReportingProperties = false,
  'data-testid': testId,
}: TemplateBodyInputProps) => {
  const pickerId = useId()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [query, setQuery] = useState<string | undefined>(undefined)
  const [activeIndex, setActiveIndex] = useState(0)

  const isOpen = query !== undefined
  const options = isOpen ? filterCanonicalOptions(query, { hasReportingProperties }) : []

  const updateQueryFromCursor = (el: HTMLTextAreaElement) => {
    setQuery(activeVariableQuery(el.value, el.selectionStart ?? el.value.length))
    setActiveIndex(0)
  }

  const selectOption = (option: CanonicalVariableOption) => {
    const el = textareaRef.current
    if (!el) return
    const cursor = el.selectionStart ?? value.length
    const { body, cursor: nextCursor } = insertCanonicalToken(value, cursor, option.token)
    onChange(body)
    setQuery(undefined)
    // The textarea hasn't re-rendered with `body` yet on this tick; placing the
    // caret has to wait until it has, or it lands at the stale `nextCursor` in
    // the old (shorter) string.
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(nextCursor, nextCursor)
    })
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!isOpen) return
    if (e.key === 'Escape') {
      e.preventDefault()
      setQuery(undefined)
      return
    }
    if (options.length === 0) return
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveIndex((i) => (i + 1) % options.length)
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIndex((i) => (i - 1 + options.length) % options.length)
        break
      case 'Enter':
        e.preventDefault()
        selectOption(options[activeIndex])
        break
    }
  }

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        className="w-full rounded-md border border-border bg-surface p-2 text-text-primary"
        rows={4}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          updateQueryFromCursor(e.target)
        }}
        onClick={(e) => updateQueryFromCursor(e.currentTarget)}
        onKeyDown={handleKeyDown}
        onKeyUp={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight')
            updateQueryFromCursor(e.currentTarget)
        }}
        onBlur={() => {
          // A picker-option mousedown prevents the default blur, so reaching
          // here means focus genuinely left the textarea — dismiss the picker.
          setQuery(undefined)
          onBlur?.()
        }}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={pickerId}
        aria-activedescendant={isOpen ? `${pickerId}-option-${activeIndex}` : undefined}
        data-testid={testId}
      />
      {isOpen && (
        <TemplateVariablePicker
          id={pickerId}
          options={options}
          activeIndex={activeIndex}
          onSelect={selectOption}
        />
      )}
    </div>
  )
}
